import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Search, X, Anchor, Loader2 } from "lucide-react";
import { normalizeString } from "@/utils/searchUtils";
import { supabase } from "@/integrations/supabase/client";
import type { Agencia } from "@/types/agencia";

// Eixos de filtro da tela. String vazia = eixo sem filtro; os selects usam
// "todos"/"todas" internamente e traduzem pra vazio ao emitir.
export interface FiltrosProjetos {
  freelancer: string;
  area: string;
  agencia: string;
  tipoContrato: string;
}

interface FiltrosControleProjetosProps {
  onFiltrosChange: (filtros: FiltrosProjetos) => void;
  areas: string[];
}

export function FiltrosControleProjetos({
  onFiltrosChange,
  areas,
}: FiltrosControleProjetosProps) {
  const [freelancer, setFreelancer] = useState("todos");
  const [area, setArea] = useState("todas");
  const [agencia, setAgencia] = useState("todas");
  const [tipoContrato, setTipoContrato] = useState("todos");
  const [searchFreelancer, setSearchFreelancer] = useState("");
  const [searchAgencia, setSearchAgencia] = useState("");

  // PERF: busca server-side (id+nome, limit 50) em vez de carregar a tabela
  // inteira de ~5,5k freelancers (6 páginas) só para popular um dropdown de filtro.
  const { data: freelancers = [], isLoading: isLoadingFreelancers } = useQuery({
    queryKey: ["freelancer-filter-options", searchFreelancer.trim()],
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async () => {
      let q = supabase
        .from("freelancers")
        .select("id, nome, is_active")
        .order("nome", { ascending: true })
        .limit(50);
      const term = searchFreelancer.trim();
      if (term) q = q.ilike("nome", `%${term}%`);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Query para carregar agências — key dedicada de opções (id+nome), isolada
  // do cache de shape completo em ["agencias"].
  const { data: allAgencias = [], isLoading: isLoadingAgencias } = useQuery({
    queryKey: ["agencias", "options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");

      if (error) throw error;
      return (data || []) as Agencia[];
    },
    staleTime: 1000 * 60 * 15, // 15 minutos
  });

  // Filtrar agências em memória conforme o termo de busca
  const agencias = useMemo(() => {
    if (!allAgencias || allAgencias.length === 0) return [];

    if (!searchAgencia.trim()) {
      // Se não há termo de busca, retorna todas ordenadas alfabeticamente
      return allAgencias
        .map((agencia) => ({ id: agencia.id, nome: agencia.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
    }

    // Busca case-insensitive e sem acentos
    const normalizedSearch = normalizeString(searchAgencia);

    return allAgencias
      .filter((agencia) => normalizeString(agencia.nome).includes(normalizedSearch))
      .map((agencia) => ({ id: agencia.id, nome: agencia.nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }));
  }, [allAgencias, searchAgencia]);

  // Filtro LIVE: emite a cada mudança (sem botão "Aplicar"). `next` sobrepõe o
  // valor recém-alterado, já que o setX ainda não refletiu no closure.
  const emitir = (next: Partial<FiltrosProjetos>) => {
    const merged = { freelancer, area, agencia, tipoContrato, ...next };
    onFiltrosChange({
      freelancer: merged.freelancer === "todos" ? "" : merged.freelancer,
      area: merged.area === "todas" ? "" : merged.area,
      agencia: merged.agencia === "todas" ? "" : merged.agencia,
      tipoContrato: merged.tipoContrato === "todos" ? "" : merged.tipoContrato,
    });
  };

  const limparFiltros = () => {
    setFreelancer("todos");
    setArea("todas");
    setAgencia("todas");
    setTipoContrato("todos");
    setSearchFreelancer("");
    setSearchAgencia("");
    onFiltrosChange({ freelancer: "", area: "", agencia: "", tipoContrato: "" });
  };

  const temFiltrosAtivos =
    freelancer !== "todos" || area !== "todas" || agencia !== "todas" || tipoContrato !== "todos";

  return (
    <FilterToolbar>
      {/* Freelancer */}
      <Select
        value={freelancer}
        onValueChange={(value) => {
          setFreelancer(value);
          emitir({ freelancer: value });
        }}
      >
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <div className="sticky top-0 p-2 bg-background border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar freelancer..."
                value={searchFreelancer}
                onChange={(e) => setSearchFreelancer(e.target.value)}
                // Impede o typeahead nativo do Radix Select de rodar nas teclas
                // digitadas aqui: ele agenda focar um item que o filtro pode
                // desmontar, gerando "Cannot read properties of null (focus)".
                // Só bloqueia caracteres imprimíveis; setas/Enter/Esc passam.
                onKeyDown={(e) => {
                  if (e.key.length === 1) e.stopPropagation();
                }}
                className="pl-8 h-7 text-xs"
              />
            </div>
          </div>
          <ScrollArea className="h-[200px]">
            <div className="p-2">
              <SelectItem value="todos">Todos os freelancers</SelectItem>
              {isLoadingFreelancers ? (
                <div className="flex flex-col items-center justify-center p-4 space-y-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Carregando freelancers...</p>
                </div>
              ) : freelancers.length > 0 ? (
                freelancers.map((f) => (
                  <SelectItem
                    key={f.id}
                    value={f.id}
                    className={f.is_active ? "" : "text-muted-foreground"}
                  >
                    {f.nome} {!f.is_active && <span className="text-red-500 ml-2">inativo</span>}
                  </SelectItem>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-xs">
                  {searchFreelancer
                    ? `Nenhum freelancer encontrado para "${searchFreelancer}"`
                    : "Nenhum freelancer encontrado"}
                </div>
              )}
            </div>
          </ScrollArea>
        </SelectContent>
      </Select>

      {/* Área */}
      <Select
        value={area}
        onValueChange={(value) => {
          setArea(value);
          emitir({ area: value });
        }}
      >
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todas">Todas as áreas</SelectItem>
          {areas.map((a) => (
            <SelectItem key={a} value={a}>
              {a}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Agência */}
      <Select
        value={agencia}
        onValueChange={(value) => {
          setAgencia(value);
          emitir({ agencia: value });
        }}
      >
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <div className="sticky top-0 p-2 bg-background border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar agência..."
                value={searchAgencia}
                onChange={(e) => setSearchAgencia(e.target.value)}
                // Ver nota no campo de busca de freelancer: evita o crash de
                // focus do typeahead nativo do Radix Select.
                onKeyDown={(e) => {
                  if (e.key.length === 1) e.stopPropagation();
                }}
                className="pl-8 h-7 text-xs"
              />
            </div>
          </div>
          <ScrollArea className="h-[200px]">
            <div className="p-2">
              <SelectItem value="todas">Todas as agências</SelectItem>
              {isLoadingAgencias ? (
                <div className="flex flex-col items-center justify-center p-4 space-y-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Carregando agências...</p>
                </div>
              ) : agencias.length > 0 ? (
                agencias.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.nome}
                  </SelectItem>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-xs">
                  {!allAgencias || allAgencias.length === 0
                    ? "Carregando agências..."
                    : searchAgencia
                      ? `Nenhuma agência encontrada para "${searchAgencia}"`
                      : "Nenhuma agência encontrada"}
                </div>
              )}
            </div>
          </ScrollArea>
        </SelectContent>
      </Select>

      {/* Tipo de Contrato */}
      <Select
        value={tipoContrato}
        onValueChange={(value) => {
          setTipoContrato(value);
          emitir({ tipoContrato: value });
        }}
      >
        <SelectTrigger className="w-full sm:w-[190px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="todos">Todos os contratos</SelectItem>
          <SelectItem value="fixo">
            <div className="flex items-center gap-2">
              <Anchor className="h-3 w-3" />
              Projetos Fixos
            </div>
          </SelectItem>
          <SelectItem value="temporario">Projetos Temporários</SelectItem>
        </SelectContent>
      </Select>

      {temFiltrosAtivos && (
        <Button
          variant="ghost"
          size="sm"
          onClick={limparFiltros}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="mr-1 h-3 w-3" />
          Limpar
        </Button>
      )}
    </FilterToolbar>
  );
}
